import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe, Search, Heart } from "lucide-react";
import SearchOverlay from "./SearchOverlay";
import { usePlaces } from "../context/PlacesContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/destinations", label: "Destinations" },
  { to: "/explore", label: "Explore" },
  { to: "/reviews", label: "Reviews" },
  { to: "/guide", label: "Guide" },
  { to: "/videos", label: "Videos" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { favs } = usePlaces();
  const location = useLocation();
  const overHero = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const solid = scrolled || !overHero;
  const textColor = solid ? "text-ink" : "text-paper";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-paper/95 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-[68px]">
          <Link to="/" className={`flex items-center gap-2.5 ${textColor}`}>
            <img
              src="/placeworthy-mark.png"
              alt="Placeworthy"
              className={`w-8 h-8 shrink-0 transition-all duration-500 ${solid ? "" : "invert"}`}
            />
            <span className="font-serif text-2xl font-semibold tracking-tight">Placeworthy</span>
            <span className={`hidden sm:inline-flex items-center gap-1 text-[10px] eyebrow ${solid ? "text-claret" : "text-paper/80"}`}>
              <Globe className="w-3 h-3" /> Canada · USA
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-[12.5px] font-medium tracking-wide link-underline transition-colors ${textColor} ${
                    isActive ? "opacity-100" : "opacity-70 hover:opacity-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className={`${textColor} opacity-80 hover:opacity-100 transition-opacity`}
            >
              <Search className="w-5 h-5" />
            </button>
            <Link to="/favourites" aria-label="Your list" className={`relative ${textColor} opacity-80 hover:opacity-100 transition-opacity`}>
              <Heart className="w-5 h-5" fill={favs.length ? "currentColor" : "none"} />
              {favs.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-claret text-paper text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {favs.length}
                </span>
              )}
            </Link>
            <a
              href="mailto:placeworthy@gmail.com"
              className={`hidden md:inline-flex items-center px-5 py-2.5 text-[12px] font-semibold tracking-wide transition-all duration-300 ${
                solid
                  ? "bg-claret text-paper hover:bg-claret-dark"
                  : "bg-paper text-ink hover:bg-white"
              }`}
            >
              Get Early Access
            </a>
            <button
              className={`lg:hidden ${textColor}`}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-[60] bg-ink text-paper transition-all duration-500 lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between h-[68px] px-6">
          <span className="flex items-center gap-2.5">
            <img src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-7 h-7 invert" />
            <span className="font-serif text-2xl font-semibold">Placeworthy</span>
          </span>
          <button onClick={() => setOpen(false)} aria-label="Close menu">
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="flex flex-col px-6 pt-8 gap-1">
          {LINKS.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className="font-serif text-4xl py-3 border-b border-paper/15 opacity-90 hover:opacity-100 hover:text-brass transition-colors"
              style={{ transitionDelay: `${i * 30}ms` }}
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href="mailto:placeworthy@gmail.com"
            className="mt-8 inline-flex justify-center items-center px-5 py-4 bg-claret text-paper text-sm font-semibold tracking-wide"
          >
            Get Early Access
          </a>
        </nav>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
